// Réglages par enseigne lus dans la configuration pgone (suiteoneconfig, fichier « beone »). Chaque clé remplace un
// réglage par workspace du BeOne historique ; la valeur par défaut est celle que BeOne appliquait quand l'enseigne
// n'était pas configurée, utilisée tant qu'une clé est absente (la configuration de production est encore partielle).

type Parser<T> = (raw: string) => T;

const flag: Parser<boolean> = raw => raw.trim() === '1' || raw.trim().toLowerCase() === 'true';
const idList: Parser<string[]> = raw => raw.split(',').map(v => v.trim()).filter(Boolean);

interface SettingDefinition<T> {
  parse: Parser<T>;
  defaultValue: T;
}

const define = <T>(parse: Parser<T>, defaultValue: T): SettingDefinition<T> => ({ parse, defaultValue });

export const BRAND_SETTINGS = {
  // Option « opération test » à la validation (BeOne : planning/validation/config/operation_test.yml)
  'module.test.operation.active': define(flag, false),
  // Module préconisations (BeOne : library/preconization/config/settings.yml)
  'module.preconisation.active': define(flag, false),
  // Badges de la fiche client (BeOne : config/fidelityProgram.yml, relationnel)
  'module.badges.active': define(flag, false),
  // Types d'identifiants considérés comme cartes de fidélité (BeOne : config/identifiers.yml, retail)
  'identifiers.retail.list': define(idList, ['1', '2', '3', '4', '5', '6', '7', '8', '9', '98']),
  // Type d'identifiant affiché comme « N° client » (BeOne : config/identifiers.yml, unique_id)
  'identifiers.unique.id.list': define(idList, [] as string[]),
  // Requêteur : types d'identifiants proposés et cochés par défaut (BeOne : config/queryIdentifiers.yml)
  'query.identifiers.default.list': define(idList, ['1']),
  'query.identifiers.default.checked': define(idList, ['1']),
} as const;

export type BrandSettingKey = keyof typeof BRAND_SETTINGS;
export type BrandSettingValue<K extends BrandSettingKey> = (typeof BRAND_SETTINGS)[K]['defaultValue'];

export const readBrandSetting = <K extends BrandSettingKey>(
  key: K,
  getRawValue: (key: string) => string | undefined,
): BrandSettingValue<K> => {
  const definition = BRAND_SETTINGS[key] as SettingDefinition<BrandSettingValue<K>>;
  const raw = getRawValue(key);
  return raw === undefined || raw.trim() === '' ? definition.defaultValue : definition.parse(raw);
};
